import React, { FC, FormEvent, useState } from 'react';
import Auth from '@aws-amplify/auth';
import { Formik, FormikHelpers } from 'formik';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Portal,
  Typography,
} from '@material-ui/core';
import { createStyles, withStyles, Theme, WithStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';

import { Loaders, ValidatedTextField, VisibilityAdornment } from 'components';
import { useNotify } from 'hooks';

import { ChangePasswordSchema, IChangePasswordValues } from '../validations';

interface IProps extends WithStyles<typeof styles> {
  open: boolean;
  onClose: () => void;
}

const COGNITO_ERROR = 'Incorrect username or password.';

const styles = (theme: Theme) =>
  createStyles({
    action: {
      display: 'flex',
      justifyContent: 'center',
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
    button: {
      width: 250,
    },
  });

const ChangePassword: FC<IProps> = props => {
  const { classes, open, onClose } = props;
  const [currentVisible, setCurrentVisible] = useState(false);
  const [newVisible, setNewVisible] = useState(false);

  const { t } = useTranslation();
  const notify = useNotify();

  const initialValues: IChangePasswordValues = {
    current_password: '',
    new_password: '',
  };

  async function handleFormikSubmit(
    values: IChangePasswordValues,
    actions: FormikHelpers<IChangePasswordValues>,
  ) {
    try {
      const user = await Auth.currentAuthenticatedUser();
      await Auth.changePassword(user, values.current_password, values.new_password);
      onClose();
      notify.info({ message: t('user:passwordChanged') });
    } catch (error) {
      error.message === COGNITO_ERROR
        ? actions.setFieldError('current_password', t('user:incorrectPassword'))
        : notify.error({ message: error.message });
    } finally {
      actions.setSubmitting(false);
    }
  }

  function renderTitle() {
    return (
      <DialogTitle disableTypography>
        <Typography variant="subtitle2">{t('user:changePassword')}</Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
    );
  }

  function renderCurrentPassword() {
    return (
      <ValidatedTextField
        type={currentVisible ? 'text' : 'password'}
        name="current_password"
        i18nKey="user:currentPassword"
        InputProps={{
          endAdornment: (
            <VisibilityAdornment
              visible={currentVisible}
              onToggle={() => setCurrentVisible(!currentVisible)}
            />
          ),
        }}
      />
    );
  }

  function renderNewPassword() {
    return (
      <ValidatedTextField
        type={newVisible ? 'text' : 'password'}
        name="new_password"
        i18nKey="user:newPassword"
        InputProps={{
          endAdornment: (
            <VisibilityAdornment visible={newVisible} onToggle={() => setNewVisible(!newVisible)} />
          ),
        }}
      />
    );
  }

  function renderAction(isSubmitting: boolean) {
    if (isSubmitting) {
      return (
        <div className={classes.action}>
          <Loaders.Standard height={38} />
        </div>
      );
    }

    return (
      <div className={classes.action}>
        <Button
          className={classes.button}
          color="primary"
          size="large"
          type="submit"
          variant="contained">
          {t('common:save')}
        </Button>
      </div>
    );
  }

  return (
    <Portal>
      <Dialog onClose={onClose} open={open}>
        {renderTitle()}
        <DialogContent>
          <Formik
            onSubmit={handleFormikSubmit}
            initialValues={initialValues}
            validationSchema={ChangePasswordSchema}>
            {({ handleSubmit, isSubmitting }) => (
              <form
                onSubmit={(event: FormEvent) => {
                  event.preventDefault();
                  event.stopPropagation();
                  handleSubmit();
                }}>
                {renderCurrentPassword()}
                {renderNewPassword()}
                {renderAction(isSubmitting)}
              </form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>
    </Portal>
  );
};

ChangePassword.defaultProps = {
  open: false,
};

export default withStyles(styles)(ChangePassword);
